import { Link } from 'react-router-dom';
import type { ClearanceDetail } from '../../../../../js/api';
import { StatusPill } from '../../../components/StatusPill';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useActingAsEntity } from '../../../hooks/useActingAsEntity';
import { useWorkflowMetadata, WorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import { getQueueNameForStatus } from '../../../utils/workflowUtils';

type ConnectionsTabProps = {
  clearance: ClearanceDetail;
};

export function ConnectionsTab({ clearance }: ConnectionsTabProps) {
  const relatedConnections = clearance.related_connections || [];
  const { statuses: connectionStatuses } = useWorkflowMetadata(WorkflowScopes.ConnectedFacility);
  // The drawer is shared by the org and staff portals; link into the one the
  // viewer is in (same pattern as the connection drawer / opportunities list).
  const { isStaff } = useActingAsEntity();
  const connectionsPath = isStaff ? '/staff/connections' : '/app/organization/connections';

  if (relatedConnections.length === 0) {
    return (
      <div className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
        No open connections found.
      </div>
    );
  }

  return (
    <div className="tw:space-y-1">
      {relatedConnections.map((connection) => {
        const queueCode =
          getQueueNameForStatus(
            connectionStatuses as WorkflowMetadata,
            connection.current_status_id
          ) || 'all';

        return (
          <div key={connection.id} className="tw:flex tw:items-center tw:justify-between tw:py-1">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <Link
                to={`${connectionsPath}?queue=${queueCode}&id=${connection.id}&search=${clearance.provider?.display_name}`}
                className="tw:text-sm tw:font-medium tw:text-left tw:text-brand-green-1
                tw:!decoration-none tw:hover:text-brand-green-0 tw:cursor-pointer tw:hover:underline
                tw:bg-transparent tw:border-0 tw:p-0 tw:w-fit"
              >
                {connection.facility?.name || '--'}
              </Link>
            </div>
            <span className="tw:ml-2 tw:inline-flex tw:items-center">
              {connection.current_status_id ? (
                <StatusPill
                  scope={WorkflowScopes.ConnectedFacility}
                  status={connection.current_status_id as StatusCodes}
                  small
                />
              ) : (
                <span className="tw:text-xs tw:text-zinc-400">--</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
